import React, { useState, useMemo } from 'react';
import { Button, Slider, Switch, Tooltip } from 'antd';
import { ReloadOutlined, EyeInvisibleOutlined, EyeOutlined, StepBackwardOutlined, StepForwardOutlined } from '@ant-design/icons';

const SIZE = 300;
const CX = SIZE / 2;
const CY = SIZE / 2;
const R_FACE = 130;
const R_TEXT = 105;

export const InteractiveClockWidget = ({ config = {}, variables = {} }) => {
    const initialTimeStr = variables.time || config.time || "09:00";
    const [initialH, initialM] = initialTimeStr.split(':').map(Number);
    const startMinutes = (initialH % 12) * 60 + initialM;

    const [totalMinutes, setTotalMinutes] = useState(startMinutes);
    const [showDigital, setShowDigital] = useState(config.showDigital ?? true);
    const [showMinuteMarks, setShowMinuteMarks] = useState(true);

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const mAngle = minutes * 6;
    const hAngle = (hours * 30) + (minutes * 0.5);

    const digitalTime = useMemo(() => {
        const hDisp = hours === 0 ? 12 : hours;
        const mDisp = minutes.toString().padStart(2, '0');
        return `${hDisp}:${mDisp}`;
    }, [hours, minutes]);

    const adjustTime = (delta) => {
        setTotalMinutes(prev => {
            let next = prev + delta;
            if (next < 0) next += 720;
            if (next >= 720) next -= 720;
            return next;
        });
    };

    const renderFace = () => {
        const numbers = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

        return (
            <g>
                <circle cx={CX} cy={CY} r={R_FACE} fill="white" stroke="#e2e8f0" strokeWidth="4" />

                {/* Ticks */}
                {Array.from({ length: 60 }).map((_, i) => {
                    const isMajor = i % 5 === 0;
                    if (!showMinuteMarks && !isMajor) return null;

                    const angle = (i * 6 - 90) * (Math.PI / 180);
                    const rInner = isMajor ? R_FACE - 15 : R_FACE - 8;
                    return (
                        <line
                            key={i}
                            x1={CX + R_FACE * Math.cos(angle)}
                            y1={CY + R_FACE * Math.sin(angle)}
                            x2={CX + rInner * Math.cos(angle)}
                            y2={CY + rInner * Math.sin(angle)}
                            stroke={isMajor ? "#334155" : "#94a3b8"}
                            strokeWidth={isMajor ? 3 : 1}
                        />
                    );
                })}

                {/* Numbers */}
                {numbers.map((num) => {
                    const numAngle = (num * 30 - 90) * (Math.PI / 180);
                    return (
                        <text
                            key={num}
                            x={CX + R_TEXT * Math.cos(numAngle)}
                            y={CY + R_TEXT * Math.sin(numAngle)}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize="20"
                            fontWeight="bold"
                            fill="#475569"
                            style={{ userSelect: 'none' }}
                        >
                            {num}
                        </text>
                    );
                })}
            </g>
        );
    };

    return (
        <div className="flexColumn mediumGap ch-width-100">
            <div className="ch-emph-block" style={{ padding: '20px 0', overflow: 'hidden' }}>
                <div className="flexSpaceBetween flexAlignCentre padding-x--m margin-b--m">
                    <div className="ch-text-bold text-muted uppercase tracking-wide text-xs">Interactive Clock</div>
                    <div className={`ch-badge ${showDigital ? 'ch-badge-info' : 'ch-badge-warning'}`} style={{ minWidth: 80, textAlign: 'center', fontSize: 16 }}>
                        {showDigital ? digitalTime : '??:??'}
                    </div>
                </div>

                <div className="flexJustifyCentre" style={{ height: 320 }}>
                    <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ overflow: 'visible' }}>
                        {renderFace()}
                        {/* Hour Hand */}
                        <g transform={`rotate(${hAngle}, ${CX}, ${CY})`}>
                            <line x1={CX} y1={CY} x2={CX + 70} y2={CY} stroke="#1e293b" strokeWidth="6" strokeLinecap="round" />
                            <line x1={CX} y1={CY} x2={CX - 15} y2={CY} stroke="#1e293b" strokeWidth="6" strokeLinecap="round" />
                        </g>
                        {/* Minute Hand */}
                        <g transform={`rotate(${mAngle}, ${CX}, ${CY})`}>
                            <line x1={CX} y1={CY} x2={CX + 110} y2={CY} stroke="#ef4444" strokeWidth="4" strokeLinecap="round" />
                            <line x1={CX} y1={CY} x2={CX - 20} y2={CY} stroke="#ef4444" strokeWidth="4" strokeLinecap="round" />
                        </g>
                        <circle cx={CX} cy={CY} r="6" fill="#ef4444" stroke="white" strokeWidth="2" />
                    </svg>
                </div>
            </div>

            <div className="ch-card padding--m bg-white border-all--s">
                <div className="flexColumn gap-medium">
                    <div>
                        <div className="flexSpaceBetween ch-text-small text-muted margin-b--xs">
                            <span>Time Scrubber</span>
                            <span>{Math.floor(totalMinutes/60)}h {totalMinutes%60}m</span>
                        </div>
                        <Slider min={0} max={719} value={totalMinutes} onChange={setTotalMinutes} tooltip={{ formatter: null }} />
                    </div>

                    <div className="flexSpaceBetween flexAlignCentre border-t--s padding-t--s">
                        <div className="flexAlignCentre gap-small">
                            <Button icon={<StepBackwardOutlined />} onClick={() => adjustTime(-5)}>-5m</Button>
                            <Button icon={<StepForwardOutlined />} onClick={() => adjustTime(5)}>+5m</Button>
                            <Switch
                                size="small"
                                checked={showMinuteMarks}
                                onChange={setShowMinuteMarks}
                                checkedChildren="Ticks"
                                unCheckedChildren="Ticks"
                            />
                        </div>
                        <div className="flexAlignCentre gap-small">
                            <Tooltip title={showDigital ? "Hide Digital Time" : "Show Digital Time"}>
                                <Button shape="circle" icon={showDigital ? <EyeInvisibleOutlined /> : <EyeOutlined />} onClick={() => setShowDigital(!showDigital)} />
                            </Tooltip>
                            <Tooltip title="Reset to start time">
                                <Button shape="circle" icon={<ReloadOutlined />} onClick={() => setTotalMinutes(startMinutes)} />
                            </Tooltip>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};